import { Injectable } from '@angular/core';
import {
  DateUtil,
  IRedirectShopperIframeResponse,
  IThreeDSVerifyRequest,
  ThreeDSStatus,
} from '@nibtravel/shared/util/models';
import { PolicyHolderService } from '@nibtravel/shared/util/service';
import { format, getMonth, getYear, getDate } from 'date-fns';
import { cloneDeep } from 'lodash-es';
import { IQuoteState, IQuoteInput } from '../models';
import { HttpParams } from '@angular/common/http';
import { CookiesDataService } from '@nibtravel/shared/data-access/cookies';
import { UrlParamService } from '../../../../../../../apps/tvl-qqc/src/app/urlParam/urlParam.service';
import { Store } from '@ngxs/store';
import { NewRelicAnalytics } from '../../../../newrelic-analytics/src/lib/newrelic.action';

@Injectable({ providedIn: 'root' })
export class QuoteHelperService {
  constructor(
    private policyHolderHelper: PolicyHolderService,
    private cookieService: CookiesDataService,
    private urlParamService: UrlParamService,
    private store: Store
  ) {}

  /**
   * Use this method to update the quote object to ensure the quote object
   * has the expected shape by the quote apis.
   */
  prepareQuoteObject(quote: IQuoteState, resetPlans = true): IQuoteState {
    // Clone quote as we don't want to mutate current state
    quote = cloneDeep(quote);
    this.store.dispatch(
      new NewRelicAnalytics.SetNewRelicActionAttributes({ quoteId: quote.id })
    );

    // Reset planId before getting quote
    if (resetPlans) {
      quote = { ...this.resetPlans(quote) };
    }

    // At the moment, the destination selector is a single selection dropdown
    // This is needed for AMT
    if (!Array.isArray(quote.input.destinationCountryCodes)) {
      quote.input.destinationCountryCodes = [
        quote.input.destinationCountryCodes,
      ];
    }
    quote.input.policyHolders = this.policyHolderHelper.getValidPolicyholder(
      quote.input.policyHolders
    );

    if (
      !quote.input.footprintsDonation?.donationAmount ||
      quote.input.footprintsDonation?.donationAmount === 0
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { footprintsDonation, ...values } = quote.input;
      quote.input = values;
    }

    // Ensure date format
    quote.input = this.dateFieldHandler(quote.input);

    return quote;
  }

  public createUrlQuoteParams(
    quote: IQuoteState,
    data?: { toKosmos?: boolean }
  ): HttpParams {
    const {
      day: depDay,
      month: depMonth,
      year: depYear,
    } = this.getDateParts(quote.input.departureDate);
    const {
      day: retDay,
      month: retMonth,
      year: retYear,
    } = this.getDateParts(quote.input.coverEndDate);

    const ages = quote.input.policyHolders.map((ph) => ph.age).join(',');
    const countryCodes = Array.isArray(quote.input.destinationCountryCodes)
      ? quote.input.destinationCountryCodes.join(',')
      : [quote.input.destinationCountryCodes];
    const quoteType = quote.input.quoteType;
    const dependentAge = quote.input.policyHolders
      ?.filter((policyHolder) => policyHolder.isDependent)
      ?.map((policyHolder) => policyHolder.age)
      .join(',');
    const quoteParams: Record<string, any> = {
      depDay,
      depMonth,
      depYear,
      retDay,
      retMonth,
      retYear,
      ages,
      countryCodes,
      quoteType,
      dependentAge,
    };

    this.appendPromoParams(quoteParams);

    if (data?.toKosmos) {
      quoteParams.toKosmos = true;
    }
    return new HttpParams({
      fromObject: quoteParams,
    });
  }

  private appendPromoParams(quoteParams: Record<string, any>) {
    const campaignCode =
      this.urlParamService.getUrlParameter('campaignCode') ||
      this.urlParamService.getUrlParameter('campaign');

    const partnerCode = this.cookieService.get('PartnerCode');

    if (partnerCode) {
      quoteParams.partnerCode = partnerCode;
    }

    if (campaignCode) {
      quoteParams.campaignCode = campaignCode;
    }

    return quoteParams;
  }

  private getDateParts(value: string | undefined): {
    day: number;
    month: number;
    year: number;
  } {
    const dateValue = new Date(value ?? '');

    return {
      day: getDate(dateValue),
      month: getMonth(dateValue) + 1,
      year: getYear(dateValue),
    };
  }

  getThreeDSRequestData(
    type: ThreeDSStatus,
    data: string,
    paymentData: string
  ): string {
    let result: IThreeDSVerifyRequest | undefined;
    const response = JSON.parse(data);
    switch (type) {
      case ThreeDSStatus.IDENTIFY:
        result = this.getIdentifyRequest(paymentData);
        break;
      case ThreeDSStatus.CHALLENGE:
        result = this.getChallengeRequest(response, paymentData);
        break;
      case ThreeDSStatus.REDIRECT:
        result = this.getRedirectRequest(response, paymentData);
        break;
    }
    return JSON.stringify(result);
  }

  updateDisplayDateFormat(date: string | undefined): string | undefined {
    if (!date) {
      return undefined;
    }
    return format(new Date(date), DateUtil.DisplayFormat);
  }

  dateFieldHandler(input: IQuoteInput): IQuoteInput {
    if (input.departureDate) {
      input.departureDate = format(
        new Date(input.departureDate),
        DateUtil.IsoDateFormat
      );
    }

    if (input.coverEndDate) {
      input.coverEndDate = format(
        new Date(input.coverEndDate),
        DateUtil.IsoDateFormat
      );
    }
    return input;
  }

  private getIdentifyRequest(paymentData: string): IThreeDSVerifyRequest {
    const fingerPrint = btoa(JSON.stringify({ threeDSCompInd: 'Y' }));
    return {
      fingerprintResult: fingerPrint,
      paymentData: paymentData,
    };
  }

  private getChallengeRequest(
    response: any,
    paymentData: string
  ): IThreeDSVerifyRequest | undefined {
    if (response && response.cres) {
      const parseData = JSON.parse(atob(response.cres));
      const challengeResult = btoa(
        JSON.stringify({ transStatus: parseData.transStatus })
      );
      return {
        challengeResult: challengeResult,
        paymentData: paymentData,
      };
    }
    return undefined;
  }

  private getRedirectRequest(
    response: IRedirectShopperIframeResponse,
    paymentData: string
  ): IThreeDSVerifyRequest | undefined {
    if (response && response.MD && response.PaRes) {
      return {
        md: response.MD,
        paRes: response.PaRes,
        paymentData: paymentData,
      };
    }
    return undefined;
  }

  private resetPlans(quote: IQuoteState): IQuoteState {
    return {
      ...quote,
      input: {
        ...quote.input,
        planId: undefined,
      },
    };
  }
}
